<template>
  <ButtonMenu ref="buttonMenuEl" :offset="[0, 4]" placement="bottom-end">
    <template #btn>
      <BaseButton kind="auxiliary">
        <template #icon>
          <MoreIcon />
        </template>
      </BaseButton>
    </template>
    <template #content>
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon> <component :is="action.icon" /> </template
        ><template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </ButtonMenu>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
} from "vue";

import type { FullEvent, FullTask } from "@ctrl/controller-rpc";

import { allocationActions, eventActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseCancelButton from "@/components/base/BaseCancelButton.vue";
import {
  AllocationContextActions,
  EventContextActions,
} from "@/components/context-menu/context-actions";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import { isPassiveEvent, useEventInfo } from "@/components/eventInfo";
import ArchiveIcon from "@/components/icons/ArchiveIcon.vue";
import CompleteIcon from "@/components/icons/CompleteIcon.vue";
import CrossedEyeIcon from "@/components/icons/CrossedEyeIcon.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import OpenIcon from "@/components/icons/OpenIcon.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  components: {
    ArchiveIcon,
    BaseButton,
    BaseCancelButton,
    ButtonMenu,
    CompleteIcon,
    CrossedEyeIcon,
    EyeIcon,
    MoreIcon,
    OpenIcon,
  },
  props: {
    event: { type: Object as PropType<FullEvent>, required: true },
    task: { type: Object as PropType<FullTask>, required: false },
  },

  async setup(props) {
    const { event, task } = toRefs(props);
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const router = inject.router();
    const multipleEventsModal = inject.multipleEventsModal();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();
    const palette = inject.palette();
    const buttonMenuEl = ref<InstanceType<typeof ButtonMenu>>();
    const isShown = computed(() => !!buttonMenuEl.value?.isShown);
    const eventInfo = useEventInfo(controller, calendars, event);
    onBeforeUnmount(() =>
      eventInfo.then((eventInfo) => eventInfo.releaseEventInfo())
    );
    const { calendar, completable } = await eventInfo;
    const isIgnored = computed(() =>
      isPassiveEvent(calendar.value, event.value)
    );
    const moreActions = computed<
      EventContextActions[] | AllocationContextActions[]
    >(() =>
      task.value
        ? [
            allocationActions.open,
            ...(completable.value ? [allocationActions.complete] : []),
            allocationActions.ignore,
            { ...allocationActions.delete, tint: "red" as const },
          ]
        : [
            eventActions.open,
            ...(event.value.attendable ? [eventActions.openConference] : []),
            ...(completable.value ? [eventActions.complete] : []),
            isIgnored.value
              ? eventActions.setAsActive
              : eventActions.setAsPassive,
            ...(!event.value.draft ? [eventActions.duplicate] : []),
            { ...eventActions.delete, tint: "red" as const },
          ]
    );
    const getActionFunc = (
      action: EventContextActions | AllocationContextActions,
      mouseEvent?: MouseEvent
    ) =>
      task.value
        ? action.action(
            {
              type: "allocation",
              value: { ...task.value, event: event.value },
            },
            {
              controller,
              logger,
              profile,
              router,
              simpleModal,
              multipleEventsModal,
              menu,
              palette,
              clickEvent: mouseEvent,
            }
          )
        : action.action(
            { type: "event", value: event.value },
            {
              controller,
              logger,
              profile,
              router,
              simpleModal,
              multipleEventsModal,
              menu,
              palette,
              clickEvent: mouseEvent,
            }
          );
    return { getActionFunc, isShown, moreActions, buttonMenuEl };
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/context-actions.scss";
</style>
